import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutGrid, 
  FileText, 
  BarChart2, 
  Inbox, 
  HelpCircle, 
  Bell, 
  User, 
  LogOut 
} from 'lucide-react';
import { useAuth } from "../../../../features/auth/hooks";
import { useTranslation } from 'react-i18next';
import { useConsumerDetail } from '../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useNotifications } from '../../../notification/NotificationContext';

interface SidenavProps {}

export const Sidenav: React.FC<SidenavProps> = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const {data:consumerInfo}=useConsumerDetail({
    consumer_no: getLoginDataFromStorage().remoteConsumerNumber,
    remote_utility_id:getRemoteUtilityId()
  })
  
  const { firstName, lastName, remoteConsumerNumber } = getLoginDataFromStorage();
  const { unreadCount } = useNotifications();
  
  const userName = `${firstName || ''} ${lastName || ''}`.trim() || 'Guest User';
  const userInitials = ((firstName?.charAt(0) || '') + (lastName?.charAt(0) || '')).toUpperCase() || 'GU';
  const userAccount = remoteConsumerNumber || 'N/A';
  const notificationCount = unreadCount;

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const NavItem = ({ icon: Icon, label, path, badge }: { icon: any, label: string, path: string, badge?: number }) => {
    const active = isActive(path);
    
    return (
      <Link 
        to={path} 
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          active 
            ? 'bg-[#f0f7ff] text-blue-600' 
            : 'text-slate-600 hover:bg-slate-50'
        }`}
      >
        <Icon className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-slate-500'}`} />
        <span>{label}</span>
        {active && !badge && (
          <div className="w-1.5 h-1.5 rounded-full bg-blue-600 ml-auto"></div>
        )}
        {badge !== undefined && badge > 0 && (
          <div className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
            {badge}
          </div>
        )}
      </Link>
    );
  };

  const NavGroup = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <div className="mb-2">
      <h3 className="text-[11px] font-bold tracking-wider text-slate-400 mb-2 mt-6 px-4 uppercase">
        {title}
      </h3>
      <div className="space-y-1">
        {children}
      </div>
    </div>
  );

  return (
    <aside className="bg-white fixed top-16 left-0 h-[calc(100vh-64px)] shadow-[1px_0_10px_rgba(0,0,0,0.03)] w-[260px] flex flex-col font-sans shrink-0 border-r border-slate-100 z-40">
      
      {/* Scrollable Navigation Area */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 scrollbar-hide">
        
        <NavGroup title={t("Overview")}>
          <NavItem icon={LayoutGrid} label={t("Dashboard")} path="/dashboard" />
        </NavGroup>

        <NavGroup title={t("Utility Services")}>
          <NavItem icon={FileText} label={t("My Bills")} path="/dashboard/billing" />
          <NavItem icon={BarChart2} label={t("Consumption")} path="/consumption" />
        </NavGroup>

        <NavGroup title={t("Support")}>
          <NavItem icon={Inbox} label={t("My Requests")} path="/dashboard/requests" />
          <NavItem icon={HelpCircle} label={t("Support Center")} path="/support-center" />
        </NavGroup>

        <NavGroup title={t("Account")}>
          <NavItem icon={Bell} label={t("Notifications")} path="/notification" badge={notificationCount} />
          <NavItem icon={User} label={t("Profile")} path="/profile" />
        </NavGroup>

        {consumerInfo?.result?.ownershipDisplay?.toLowerCase() === 'master' && (
          <NavGroup title={t("Master Account")}>
            <NavItem icon={LayoutGrid} label={t("All Accounts")} path="/dashboard/all-accounts" />
          </NavGroup>
        )}

      </nav>

      {/* User Profile Footer */}
      <div className="border-t border-slate-100 p-4 mt-auto bg-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold tracking-wide">
            {userInitials}
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 leading-tight">
              {userName}
            </div>
            <div className="text-[11px] text-slate-400 font-medium">
              {userAccount}
            </div>
          </div>
        </div>
        
        <button 
          onClick={(e) => {
            e.preventDefault();
            logout();
          }}
          className="flex items-center gap-3 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors w-full px-2"
        >
          <LogOut className="w-5 h-5 text-slate-500" />
          {t("Sign Out")}
        </button>
      </div>

    </aside>
  );
};
